/*
 * Atlas Rug Sales — Responsive 16:9 Hero Correction
 *
 * Ensures the Canvify hero section renders at a 16:9 aspect ratio
 * across all devices (Desktop, Tablet, Mobile) and eliminates extra
 * whitespace below the section.
 */
(() => {
  let observer = null;
  let applying = false;
  let resizeTimer = null;

  function getRect(el) {
    return el ? el.getBoundingClientRect() : null;
  }

  function findLayers(video) {
    if (!video) return {};

    const chain = [];
    let el = video;
    while (el && el.id !== 'root') {
      chain.push(el);
      el = el.parentElement;
    }

    // Media layer: nearest relative ancestor with explicit large dimensions / transform.
    const media = chain.find((node) => {
      if (node === video) return false;
      const cs = getComputedStyle(node);
      const r = getRect(node);
      return (
        cs.position === 'relative' &&
        r &&
        r.width > window.innerWidth * 1.25 &&
        r.height > 200
      );
    });

    // Frame: nearest overflow-hidden ancestor below the oversized absolute group.
    const frame = chain.find((node) => {
      if (node === video) return false;
      const cs = getComputedStyle(node);
      const r = getRect(node);
      return (
        (cs.overflow === 'hidden' || cs.overflowX === 'hidden') &&
        r &&
        r.height > 200
      );
    });

    // Outer group: first absolute ancestor substantially larger than the viewport.
    const outer = chain.find((node) => {
      const cs = getComputedStyle(node);
      const r = getRect(node);
      return (
        cs.position === 'absolute' &&
        r &&
        r.width > window.innerWidth * 1.25 &&
        r.height > 400
      );
    });

    return { media, frame, outer };
  }

  function applyFix() {
    if (applying) return false;

    const root = document.querySelector('#root');
    const canvifyMain = document.querySelector('#canvify-main');
    const video = root?.querySelector('video');
    if (!root || !canvifyMain || !video) return false;

    // Use findLayers to find specific wrappers that need positioning resets
    const { outer, frame, media } = findLayers(video);

    applying = true;
    if (observer) observer.disconnect();
    
    try {
      // Calculate exact 16:9 pixel height
      const heroWidth = canvifyMain.parentElement ? canvifyMain.parentElement.clientWidth : window.innerWidth;
      const heroHeight = (heroWidth * 9) / 16;
      const pxHeight = heroHeight + 'px';

      // 1. Traverse from the video's direct parent up to canvifyMain
      //    Force every single container to collapse to the new height.
      let currentEl = video.parentElement;
      while (currentEl && currentEl !== document.body) {
        currentEl.style.setProperty('height', pxHeight, 'important');
        currentEl.style.setProperty('min-height', pxHeight, 'important');
        currentEl.style.setProperty('max-height', pxHeight, 'important');
        currentEl.style.setProperty('padding-bottom', '0', 'important');
        currentEl.style.setProperty('margin-bottom', '0', 'important');
        
        if (currentEl.id === 'canvify-main') break;
        currentEl = currentEl.parentElement;
      }

      // 2. Ensure root and main explicitly hide overflow so nothing pushes content down
      canvifyMain.style.setProperty('overflow', 'hidden', 'important');
      root.style.setProperty('overflow', 'hidden', 'important');

      // 3. Reset transforms and absolute positioning for Canvify inner layers
      if (outer) {
        outer.style.setProperty('width', '100%', 'important');
        outer.style.setProperty('top', '0', 'important');
        outer.style.setProperty('left', '0', 'important');
        outer.style.setProperty('transform', 'none', 'important');
      }
      
      if (frame) {
        frame.style.setProperty('width', '100%', 'important');
        frame.style.setProperty('overflow', 'hidden', 'important');
      }
      
      if (media) {
        media.style.setProperty('width', '100%', 'important');
        media.style.setProperty('top', '0', 'important');
        media.style.setProperty('left', '0', 'important');
        media.style.setProperty('transform', 'none', 'important');
      }

      // 4. Make the video itself fill the container fully using cover
      video.style.setProperty('width', '100%', 'important');
      video.style.setProperty('height', '100%', 'important');
      video.style.setProperty('object-fit', 'cover', 'important');
      video.style.setProperty('position', 'absolute', 'important');
      video.style.setProperty('top', '0', 'important');
      video.style.setProperty('left', '0', 'important');
      video.style.setProperty('transform', 'none', 'important');

      return true;
    } finally {
      if (observer) {
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: false
        });
      }
      applying = false;
    }
  }

  function applyRepeatedly() {
    [0, 100, 250, 500, 900, 1500, 2500, 4000].forEach((delay) => {
      setTimeout(() => requestAnimationFrame(applyFix), delay);
    });
  }

  function startObserver() {
    const root = document.querySelector('#root');
    if (!root || observer) return;

    observer = new MutationObserver(() => {
      if (!applying) requestAnimationFrame(applyFix);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }

  function init() {
    applyRepeatedly();
    setTimeout(startObserver, 300);
    setTimeout(startObserver, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('load', applyRepeatedly, { once: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      requestAnimationFrame(applyFix);
      applyRepeatedly();
    }, 100);
  });
})();
